// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { Badge } from '@mantine/core';

import { type EvolveIconName, EvolveIcon } from 'assets/icons/EvolveIcon';
import type { BadgeSize } from 'components/Mantine/StatusBadge';
import { isNil } from 'helpers/isNotNil';

import classes from './badge-button.module.css';

type Props = {
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  icon?: EvolveIconName;
  size?: BadgeSize;
  /**
   * Whether or not to stop propagation of the click event.
   *
   * @defaultValue true
   */
  stopPropagation?: boolean;
} & Badge.Props;

export const BadgeButton = ({ icon, children, onClick, stopPropagation = true, ...buttonProps }: Props) => (
  <Badge
    leftSection={icon ? <EvolveIcon icon={icon} size="sm" /> : null}
    variant="light"
    px="xs"
    pr={children && icon ? 'sm' : undefined}
    classNames={{
      root: classes.root,
      // This ensures that if the badge is only an icon, the badge won't have extra empty space on the right
      section: isNil(children) ? classes.iconOnly : undefined,
    }}
    onClick={(e) => {
      if (stopPropagation) {
        e.stopPropagation();
        e.preventDefault();
      }
      onClick?.(e);
    }}
    {...buttonProps}
  >
    {children}
  </Badge>
);
